// Drafts' second tab: every answer written in a draft, plus ones added here,
// sorted into themes (utils/answerBank.ts). A card opens to read, copy or edit
// the bank's copy; drafts never change from here.

import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Trash2 } from "lucide-react";
import { addDoc, collection, deleteDoc, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { cn } from "../lib/utils";
import { TAG } from "./applicationShared";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import {
  MELT_BUTTON,
  MELT_BUTTON_QUIET,
  MELT_CHIP,
  MELT_CHIP_ON,
  MELT_DIALOG,
  MELT_INPUT,
  MELT_LABEL,
  MELT_TEXTAREA,
} from "./ui/melt";
import { THEMES, THEME_LABEL, matchesAnswer, themeOf, usedLabel, type BankAnswer, type Theme } from "../utils/answerBank";

const CARD = "rounded-2xl border border-melt-line bg-melt-paper";
const THEME_TAG = cn(TAG, "bg-melt-text1/[.06] text-melt-text2");

const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

const bankRef = () => {
  const user = auth.currentUser;
  return user ? collection(db, "users", user.uid, "answerBank") : null;
};

type Props = {
  items: BankAnswer[];
  search: string;
  /** The + in the top bar was pressed */
  adding: boolean;
  onAddingDone: () => void;
};

export default function AnswerBank({ items, search, adding, onAddingDone }: Props) {
  const [theme, setTheme] = useState<Theme | "all">("all");
  const [openKey, setOpenKey] = useState<string | null>(null);

  const found = useMemo(() => items.filter((item) => matchesAnswer(item, search)), [items, search]);
  const shown = theme === "all" ? found : found.filter((item) => item.theme === theme);
  const count = (id: Theme) => found.filter((item) => item.theme === id).length;
  const opened = openKey ? items.find((item) => item.key === openKey) : undefined;

  const dialog = (
    <AnswerDialog
      open={adding || !!opened}
      item={opened}
      startTheme={theme === "all" ? undefined : theme}
      onClose={() => {
        setOpenKey(null);
        onAddingDone();
      }}
    />
  );

  if (!items.length) {
    return (
      <>
        <div className="max-w-[300px]">
                    <p className="text-[15px] font-semibold text-melt-text1">No answers yet</p>
          <p className="mt-1 max-w-[28ch] text-[13px] leading-[1.45] text-melt-text2">
            Answers you write in drafts collect here, sorted by what they’re about. Add one of your own with the + up top.
          </p>
        </div>
        {dialog}
      </>
    );
  }

  const filter = (id: Theme | "all", label: string, n: number) => (
    <button
      key={id}
      type="button"
      onClick={() => setTheme(id)}
      aria-pressed={theme === id}
      className={cn(
        "flex flex-none items-center justify-between gap-3 whitespace-nowrap rounded-[9px] px-3 py-2 text-left text-[13.5px] transition-colors lg:w-full",
        theme === id
          ? "bg-melt-text1/[.06] font-medium text-melt-text1"
          : "text-melt-text2 hover:bg-melt-text1/[.04] hover:text-melt-text1",
        !n && theme !== id && "text-melt-text3",
      )}
    >
      {label}
      <span className="font-plex text-[11px] text-melt-text3">{n}</span>
    </button>
  );

  return (
    <div className="grid w-full items-start gap-5 lg:grid-cols-[210px_minmax(0,1fr)]">
      <nav
        aria-label="Themes"
        className="scrollbar-thin -mx-1 flex gap-1 overflow-x-auto px-1 lg:sticky lg:top-0 lg:mx-0 lg:flex-col lg:px-0"
      >
        {filter("all", "All answers", found.length)}
        <p className="hidden px-3 pb-1 pt-4 font-plex text-[10.5px] font-medium uppercase tracking-[.1em] text-melt-text3 lg:block">
          Themes
        </p>
        {THEMES.map((t) => filter(t.id, t.label, count(t.id)))}
      </nav>

      <section className="min-w-0">
        {shown.length === 0 ? (
          <p className={cn(CARD, "px-4 py-3.5 text-[13px] text-melt-text3")}>
            <span className="font-medium text-melt-text2">{search.trim() ? "No matches" : "Nothing here yet"}</span>
            {!search.trim() && " · Answers about this show up as you write them in drafts."}
          </p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {shown.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setOpenKey(item.key)}
                className={cn(CARD, "flex flex-col p-4 text-left transition-colors hover:border-melt-text1/25")}
              >
                <h3 className="line-clamp-2 text-[14.5px] font-semibold leading-snug text-melt-text1">{item.title}</h3>
                <p className="mt-1.5 line-clamp-3 whitespace-pre-line text-[13px] leading-[1.5] text-melt-text2">
                  {item.answer || <span className="text-melt-text3">No answer written</span>}
                </p>
                <div className="mt-auto flex items-center justify-between gap-3 pt-3">
                  {/* In a theme every card has the same one */}
                  {theme === "all" && <span className={THEME_TAG}>{THEME_LABEL[item.theme]}</span>}
                  <span className="ml-auto whitespace-nowrap font-plex text-[11px] text-melt-text3">{usedLabel(item)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {dialog}
    </div>
  );
}

// ---------------------------------------------------------------- one answer

function AnswerDialog({
  open,
  item,
  startTheme,
  onClose,
}: {
  open: boolean;
  /** Undefined: a new answer of their own */
  item?: BankAnswer;
  startTheme?: Theme;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [answer, setAnswer] = useState("");
  const [theme, setTheme] = useState<Theme | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(item?.title ?? "");
    setAnswer(item?.answer ?? "");
    setTheme(item?.theme ?? startTheme ?? null);
    setSaving(false);
    setError("");
    setCopied(false);
    setConfirmDelete(false);
    // Only when it opens: a save elsewhere shouldn't wipe what's being typed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.key]);

  if (!open) return null;

  const changed = !item || title.trim() !== item.title || answer !== item.answer || theme !== item.theme;
  const canSave = Boolean(title.trim() && answer.trim()) && changed;

  const save = async () => {
    const col = bankRef();
    if (!col || !canSave || saving) return;
    setSaving(true);
    setError("");
    const fields = {
      title: title.trim(),
      answer,
      theme: theme ?? themeOf(title),
      updatedAt: serverTimestamp(),
    };
    try {
      if (!item) await addDoc(col, { ...fields, createdAt: serverTimestamp() });
      else if (item.from) await setDoc(doc(col, `q_${item.from}`), { ...fields, from: item.from }, { merge: true });
      else await updateDoc(doc(col, item.docId!), fields);
      onClose();
    } catch (e) {
      console.error("Error saving answer:", e);
      setError("Couldn’t save. Try again.");
      setSaving(false);
    }
  };

  const remove = async () => {
    const col = bankRef();
    if (!col || !item) return;
    try {
      // A draft's answer only leaves the bank; the draft keeps it
      if (item.from)
        await setDoc(
          doc(col, `q_${item.from}`),
          { from: item.from, hidden: true, updatedAt: serverTimestamp() },
          { merge: true },
        );
      else await deleteDoc(doc(col, item.docId!));
      onClose();
    } catch (e) {
      console.error("Error deleting answer:", e);
      setError("Couldn’t delete. Try again.");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Couldn’t copy. Select the text instead.");
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className={cn(MELT_DIALOG, "max-h-[90vh] max-w-[620px] overflow-y-auto px-7 pb-6 pt-7")}>
        <DialogTitle className="text-xl font-semibold tracking-[-.02em] text-melt-text1">
          {item ? "Answer" : "New answer"}
        </DialogTitle>
        <DialogDescription className="mt-1 text-[13px] text-melt-text2">
          {item?.uses.length
            ? `${usedLabel(item)}: ${item.uses.map((u) => u.company).join(", ")}. Changes here stay in the bank; your drafts keep what you wrote.`
            : "A story or answer to reuse in applications."}
        </DialogDescription>

        <div className="mt-5 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className={MELT_LABEL}>Question or title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Why fintech, or Pricing change at the campus café"
              className={MELT_INPUT}
              autoFocus={!item}
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className={MELT_LABEL}>Theme</span>
            <div className="flex flex-wrap gap-1.5">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={theme === t.id}
                  onClick={() => setTheme(t.id)}
                  className={cn(MELT_CHIP, "px-3 py-1.5 text-[13px]", theme === t.id && MELT_CHIP_ON)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="flex items-center justify-between">
              <span className={MELT_LABEL}>Answer</span>
              <span className="font-plex text-[11px] text-melt-text3">{countWords(answer)} words</span>
            </span>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={9}
              placeholder="Write it once, reuse it everywhere"
              className={MELT_TEXTAREA}
            />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {item &&
            (confirmDelete ? (
              <span className="flex items-center gap-1 text-[13px] text-melt-text2">
                {item.from ? "Take it out of the bank?" : "Delete for good?"}
                <button
                  type="button"
                  onClick={remove}
                  className="rounded-[8px] px-2 py-1 font-medium text-[#A5492E] hover:bg-[#A5492E]/[.07]"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-[8px] px-2 py-1 hover:bg-melt-text1/[.05]"
                >
                  Keep
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 rounded-[9px] px-2 py-1.5 text-[13px] font-medium text-[#A5492E] transition-colors hover:bg-[#A5492E]/[.07]"
              >
                <Trash2 size={14} strokeWidth={1.75} />
                Delete
              </button>
            ))}
          {error && <span className="text-[13px] text-[#A5492E]">{error}</span>}
          <div className="ml-auto flex items-center gap-2">
            {item && (
              <button
                type="button"
                onClick={copy}
                disabled={!answer.trim()}
                className={cn(MELT_BUTTON_QUIET, "h-10 gap-1.5 px-3 text-sm")}
              >
                {copied ? <Check size={15} strokeWidth={1.75} /> : <Copy size={15} strokeWidth={1.75} />}
                {copied ? "Copied" : "Copy"}
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={!canSave || saving}
              className={cn(MELT_BUTTON, "h-10 rounded-[10px] px-4 text-sm")}
            >
              {saving ? "Saving…" : item ? "Save" : "Add answer"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
